import { useEffect, useState } from "react";
import { PersonalQR } from "./PersonalQR";
import { useQRScanner, parseScanPayload, type ParsedScan } from "./useQRScanner";

type Props = {
  /** Real URL payload to render in the QR. Generated via `makeScanPayload`. */
  myPayload: string;
  /** Called when a valid payload is decoded (either via scan or paste). */
  onScan: (parsed: ParsedScan, raw: string) => void;
  /** Defaults to "your QR — show this to someone" */
  showLabel?: string;
  /** Defaults to "scan a QR" */
  scanLabel?: string;
  qrSize?: number;
  /** When false, hides the paste-fallback form (rare). Default true. */
  pasteFallback?: boolean;
  /** Optional className for the outer container. */
  className?: string;
  /**
   * When provided, the show/hide-QR preference is persisted to localStorage
   * under this key. Pass `${config.storagePrefix}:qr:visible` to keep one
   * setting per app, or omit to use ephemeral session state only.
   */
  persistVisibilityKey?: string;
  /** Initial visibility of the QR side (default true). */
  defaultVisible?: boolean;
};

/**
 * Pre-baked "show your QR + scan someone else's QR + paste fallback" widget
 * used by every viral mesh-* app.
 *
 * The QR encodes a REAL HTTPS URL (generated by `makeScanPayload`) so any
 * native phone camera can open the live app and auto-join the right room
 * — no in-app scanner needed for the cold-start invite.
 *
 * Includes a 👁 toggle that hides the QR side (and the scanner toggle) so
 * users can focus on the room state. Visibility is persisted per-app when
 * `persistVisibilityKey` is supplied.
 */
export function QRExchange({
  myPayload,
  onScan,
  showLabel = "your QR — show this to someone",
  scanLabel = "scan a QR",
  qrSize = 192,
  pasteFallback = true,
  className,
  persistVisibilityKey,
  defaultVisible = true,
}: Props) {
  const [showScanner, setShowScanner] = useState(false);
  const [paste, setPaste] = useState("");
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [showQR, setShowQR] = useState<boolean>(() => {
    if (!persistVisibilityKey || typeof window === "undefined") return defaultVisible;
    try {
      const raw = localStorage.getItem(persistVisibilityKey);
      if (raw === null) return defaultVisible;
      return raw === "1";
    } catch {
      return defaultVisible;
    }
  });

  useEffect(() => {
    if (!persistVisibilityKey || typeof window === "undefined") return;
    try {
      localStorage.setItem(persistVisibilityKey, showQR ? "1" : "0");
    } catch {
      /* ignore quota errors */
    }
  }, [showQR, persistVisibilityKey]);

  const scanner = useQRScanner({
    onScan: (r) => {
      const parsed = parseScanPayload(r.text);
      if (parsed) {
        onScan(parsed, r.text);
        scanner.stop();
        setShowScanner(false);
      }
    },
  });

  const handlePaste = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseScanPayload(paste.trim());
    if (!parsed) {
      setPasteError("not a recognized payload (expected URL or mesh://)");
      return;
    }
    setPasteError(null);
    onScan(parsed, paste.trim());
    setPaste("");
  };

  return (
    <div className={`mesh-qrx ${className ?? ""} ${showQR ? "" : "is-collapsed"}`}>
      <div className="mesh-qrx-header">
        <button
          type="button"
          className="mesh-qrx-eye"
          onClick={() => setShowQR((v) => !v)}
          aria-pressed={showQR}
          title={showQR ? "hide QR + scanner" : "show QR + scanner"}
        >
          {showQR ? "👁 hide QR" : "👁 show QR"}
        </button>
      </div>

      {showQR && (
        <>
          <div className="mesh-qrx-section">
            <div className="mesh-qrx-label">{showLabel}</div>
            <div className="mesh-qrx-qr">
              <PersonalQR payload={myPayload} size={qrSize} />
            </div>
            <details className="mesh-qrx-payload">
              <summary>copy raw payload</summary>
              <code>{myPayload}</code>
            </details>
          </div>

          <div className="mesh-qrx-section">
            <div className="mesh-qrx-actions">
              <button
                type="button"
                className="mesh-qrx-btn"
                onClick={() => {
                  if (showScanner) {
                    scanner.stop();
                    setShowScanner(false);
                  } else {
                    setShowScanner(true);
                    void scanner.start();
                  }
                }}
              >
                {showScanner ? "✗ stop scanner" : `📷 ${scanLabel}`}
              </button>
            </div>
            {showScanner && (
              <video
                ref={scanner.videoRef}
                muted
                playsInline
                autoPlay
                className="mesh-qrx-video"
              />
            )}
            {scanner.error && <p className="mesh-qrx-error">⚠ {scanner.error}</p>}
            {pasteFallback && (
              <form className="mesh-qrx-paste" onSubmit={handlePaste}>
                <input
                  value={paste}
                  onChange={(e) => setPaste(e.target.value)}
                  placeholder="or paste a payload (URL or mesh://)"
                  aria-label="paste payload"
                />
                <button type="submit" disabled={!paste.trim()}>
                  use
                </button>
              </form>
            )}
            {pasteError && <p className="mesh-qrx-error">⚠ {pasteError}</p>}
          </div>
        </>
      )}
    </div>
  );
}
